import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import type { TFunction } from 'i18next';
import { Button } from '../ui/button';
import StarRating, { scoreLabel, scoreMeaning } from './StarRating';
import {
  RATING_REASON_OTHER,
  RATING_REASONS_NEGATIVE,
  RATING_REASONS_POSITIVE,
  NEGATIVE_MAX_SCORE,
  RATING_DETAIL_MAX_LENGTH,
  type RatingReason,
  type RateInterventionBody,
} from '../../api/interventionRatingApi';

// Literal translation calls only (static i18n guard). Unknown (legacy) codes yield '' so callers skip them.
export const reasonLabel = (t: TFunction, reason: string): string => {
  switch (reason) {
    case 'appointment_missed':
      return t('Appointment missed');
    case 'not_finished_same_day':
      return t('Installation not finished on the day');
    case 'planogram_mismatch':
      return t('Does not match the planogram');
    case 'products_misplaced':
      return t('Products badly filled or sorted');
    case 'equipment_damaged':
      return t('Equipment damaged');
    case 'space_not_restored':
      return t('Space not restored');
    case 'appointment_kept':
      return t('Appointment kept');
    case 'finished_same_day':
      return t('Installation finished on the day');
    case 'planogram_compliant':
      return t('Matches the planogram');
    case 'products_sorted':
      return t('Products filled and sorted by shade');
    case 'equipment_intact':
      return t('Equipment intact');
    case 'space_left_clean':
      return t('Space left clean');
    case 'other':
      return t('Other');
    default:
      return '';
  }
};

const reasonsForScore = (score: number): readonly RatingReason[] =>
  score > NEGATIVE_MAX_SCORE ? RATING_REASONS_POSITIVE : RATING_REASONS_NEGATIVE;

interface RatingFormProps {
  issueKey: string;
  initialScore?: number;
  // Prefill when editing an already-sent rating.
  initialReasons?: string[];
  initialDetail?: string | null;
  onSubmit: (body: RateInterventionBody) => void;
  onLater: () => void;
  isPending?: boolean;
}

const RatingForm = ({
  issueKey,
  initialScore = 0,
  initialReasons = [],
  initialDetail = null,
  onSubmit,
  onLater,
  isPending = false,
}: RatingFormProps) => {
  const { t } = useTranslation();
  const otherId = useId();
  const [score, setScore] = useState(initialScore);
  // Keep only the codes valid for the opening side: legacy codes are dropped.
  const [reasons, setReasons] = useState<RatingReason[]>(() =>
    reasonsForScore(initialScore).filter((code) => initialReasons.includes(code)),
  );
  const [otherText, setOtherText] = useState(initialReasons.includes(RATING_REASON_OTHER) ? (initialDetail ?? '') : '');
  const positive = score > NEGATIVE_MAX_SCORE;
  const reasonCodes = reasonsForScore(score);
  const otherTicked = reasons.includes(RATING_REASON_OTHER);

  // Crossing the 3-star boundary switches the code set: picked codes and the Autre text no longer apply.
  const pickScore = (next: number) => {
    if ((next > NEGATIVE_MAX_SCORE) !== positive) {
      setReasons([]);
      setOtherText('');
    }
    setScore(next);
  };

  const toggleReason = (reason: RatingReason) => {
    if (reason === RATING_REASON_OTHER && otherTicked) setOtherText('');
    setReasons((current) => (current.includes(reason) ? current.filter((r) => r !== reason) : [...current, reason]));
  };

  const otherBlank = otherTicked && otherText.trim() === '';
  const submit = () => onSubmit({ score, reasons, detail: otherTicked ? otherText.trim() : null });

  return (
    <div className="flex flex-col gap-5 overflow-y-auto px-5 pb-6 pt-2 max-h-[85vh]">
      <div className="flex flex-col items-center gap-3 text-center">
        <h2 className="text-xl font-semibold text-gray-900">{t('How did the service go?')}</h2>
        <p className="text-sm text-gray-500">{issueKey}</p>
        <StarRating value={score} onChange={pickScore} size="lg" />
        <p className="min-h-[1.5rem] text-base font-medium text-gray-900">{score > 0 ? scoreLabel(t, score) : ''}</p>
        <p className="min-h-[1.25rem] text-sm text-gray-500">{score > 0 ? scoreMeaning(t, score) : ''}</p>
      </div>

      {score > 0 && (
        <>
          <div>
            <p className="mb-2 text-sm font-semibold text-gray-900">{positive ? t('What worked well?') : t('What went wrong?')}</p>
            <div className="flex flex-wrap gap-2">
              {reasonCodes.map((reason) => {
                const selected = reasons.includes(reason);
                return (
                  <button
                    key={reason}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggleReason(reason)}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                      selected ? 'border-black bg-black text-white' : 'border-gray-300 bg-white text-gray-900 hover:border-gray-500'
                    }`}
                  >
                    {reasonLabel(t, reason)}
                  </button>
                );
              })}
            </div>
          </div>

          {otherTicked && (
            <div>
              <label htmlFor={`${otherId}-text`} className="mb-2 block text-sm font-semibold text-gray-900">
                {t('Describe what you observed')}
              </label>
              <textarea
                id={`${otherId}-text`}
                value={otherText}
                maxLength={RATING_DETAIL_MAX_LENGTH}
                rows={3}
                required
                placeholder={t('Describe the finding in store: missing item, access impossible…')}
                aria-describedby={`${otherId}-hint`}
                onChange={(event) => setOtherText(event.target.value)}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <div className="mt-1 flex justify-between gap-3 text-xs text-gray-500">
                <p id={`${otherId}-hint`}>{t('Never name a technician or give initials.')}</p>
                <p aria-hidden="true">{otherText.length}/{RATING_DETAIL_MAX_LENGTH}</p>
              </div>
            </div>
          )}
        </>
      )}

      <div className="flex gap-2 rounded-xl bg-gray-50 p-4 text-xs text-gray-500">
        <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="space-y-1.5">
          <p>{t('The rating is used for supplier quality monitoring. The technician only sees an aggregated average, never your individual rating.')}</p>
          <p>{t('We rate the service, never the person: no name or initials, including in the free field.')}</p>
          <p>{t('Kept for 12 months. You can change or delete your rating from this page at any time.')}</p>
          <Link to="/privacy-policy" className="font-medium text-gray-700 underline">
            {t('Privacy Policy')}
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Button type="button" variant="secondary" size="lg" onClick={onLater} disabled={isPending}>
          {t('Later')}
        </Button>
        <Button type="button" size="lg" onClick={submit} disabled={score === 0 || otherBlank || isPending}>
          {t('Send rating')}
        </Button>
      </div>
    </div>
  );
};

export default RatingForm;
